import { logError } from "@/lib/log";
import { useCallback, useEffect, useState, useRef } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, UtensilsCrossed, QrCode, Settings as SettingsIcon, CreditCard, LogOut, ExternalLink, ShieldCheck, User, ShoppingBag, Clock, Bell, Sun, Menu, Truck, Armchair, ClipboardList, CalendarDays } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAutoPrint } from "@/hooks/useAutoPrint";
import { InstallAppButton } from "@/components/InstallAppButton";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const feats = user?.subscription?.features || {};
  const [notif, setNotif] = useState({ items: [], unread: 0 });
  const [mobileNav, setMobileNav] = useState(false);
  const timer = useRef();
  const [restName, setRestName] = useState("");
  useEffect(() => { if (feats.tables) api.get("/restaurant").then((r) => setRestName(r.data?.name || "")).catch(() => {}); }, [feats.tables]);
  useAutoPrint(!!feats.tables, restName);

  const links = [
    { to: "/dashboard", label: "Panoramica", icon: LayoutDashboard, end: true, show: true },
    { to: "/dashboard/menu", label: "Costruttore menù", icon: UtensilsCrossed, show: true },
    { to: "/dashboard/daily", label: "Menù del giorno", icon: Sun, show: !!feats.daily_menu },
    { to: "/dashboard/foodtruck", label: "FoodTruck", icon: Truck, show: !!user?.is_paid },
    { to: "/dashboard/orders", label: "Ordini", icon: ShoppingBag, show: !!feats.ordering },
    { to: "/dashboard/waitlist", label: "Lista d'attesa", icon: Clock, show: !!feats.waitlist },
    { to: "/dashboard/tables", label: "Tavoli", icon: Armchair, show: !!feats.tables },
    { to: "/dashboard/comande", label: "Comande", icon: ClipboardList, show: !!feats.tables },
    { to: "/dashboard/calendario", label: "Calendario", icon: CalendarDays, show: !!feats.tables },
    { to: "/dashboard/qr", label: "QR Code", icon: QrCode, show: true },
    { to: "/dashboard/billing", label: "Abbonamento", icon: CreditCard, show: true },
    { to: "/dashboard/account", label: "Account", icon: User, show: true },
    { to: "/dashboard/settings", label: "Impostazioni", icon: SettingsIcon, show: true },
  ].filter((l) => l.show);

  const loadNotif = useCallback(async () => {
    try { const { data } = await api.get("/notifications"); setNotif(data); }
    catch (e) { logError("Notifiche non disponibili:", e); }
  }, []);
  useEffect(() => {
    loadNotif();
    timer.current = setInterval(loadNotif, 25000);
    return () => clearInterval(timer.current);
  }, [loadNotif]);

  const markRead = async () => {
    if (notif.unread > 0) { await api.post("/notifications/read"); setNotif((n) => ({ ...n, unread: 0 })); }
  };

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#FAFBFA] text-gray-900 flex">
      <aside className="w-64 bg-white border-r border-gray-200 flex-col hidden md:flex fixed inset-y-0">
        <div className="h-20 flex items-center px-6 border-b border-gray-100">
          <Logo />
        </div>
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end}
              className={({ isActive }) => `mo-banner flex items-center gap-3 px-3 py-2.5 rounded-xl border text-sm font-semibold transition-colors ${isActive ? "mo-banner-active" : ""}`}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-").replace(/'/g, "")}`}>
              <span className="h-8 w-8 rounded-lg mo-banner-icon flex items-center justify-center shrink-0"><l.icon className="h-4 w-4" /></span> {l.label}
              {l.to === "/dashboard/orders" && notif.unread > 0 && <span className="ml-auto h-2 w-2 rounded-full bg-white ring-2 ring-[#5aa469]" />}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink to="/admin" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900" data-testid="nav-admin">
              <ShieldCheck className="h-4 w-4" /> Admin
            </NavLink>
          )}
        </nav>
        <div className="p-4 border-t border-gray-100">
          <div className="flex items-center gap-3 px-2 mb-3">
            <div className="h-9 w-9 rounded-full bg-[#5aa469] text-white flex items-center justify-center text-sm font-semibold overflow-hidden">
              {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user?.name?.[0]?.toUpperCase() || "U")}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
              <p className="text-xs text-gray-400 truncate">{user?.email}</p>
            </div>
          </div>
          <div className="mb-2"><InstallAppButton compact /></div>
          <Button onClick={doLogout} variant="ghost" className="w-full justify-start text-gray-600 hover:text-red-500 hover:bg-gray-100" data-testid="logout-btn">
            <LogOut className="h-4 w-4 mr-2" /> Esci
          </Button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 md:ml-64">
        <header className="h-16 mo-glass flex items-center justify-between px-6 sticky top-0 z-20">
          <div className="md:hidden flex items-center gap-2">
            <Sheet open={mobileNav} onOpenChange={setMobileNav}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Apri menu" data-testid="mobile-nav-toggle"><Menu className="h-5 w-5" /></Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0" data-testid="mobile-nav">
                <div className="h-16 flex items-center px-5 border-b border-gray-100"><SheetTitle className="sr-only">Menu di navigazione</SheetTitle><Logo /></div>
                <nav className="p-3 space-y-1">
                  {links.map((l) => (
                    <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setMobileNav(false)}
                      className={({ isActive }) => `mo-banner flex items-center gap-3 px-3 py-2.5 rounded-xl border text-sm font-semibold ${isActive ? "mo-banner-active" : ""}`}
                      data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-").replace(/'/g, "")}`}>
                      <l.icon className="h-4 w-4" /> {l.label}
                    </NavLink>
                  ))}
                  <div className="px-1 pt-2"><InstallAppButton compact /></div>
                  <button onClick={doLogout} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100" data-testid="mobile-logout-btn"><LogOut className="h-4 w-4" /> Esci</button>
                </nav>
              </SheetContent>
            </Sheet>
            <Logo />
          </div>
          <div className="hidden md:block" />
          <div className="flex items-center gap-2">
            <Popover onOpenChange={(o) => o && markRead()}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="icon" className="relative rounded-full border-gray-300 bg-white hover:bg-gray-50" data-testid="notif-bell">
                  <Bell className="h-4 w-4 text-gray-600" />
                  {notif.unread > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#5aa469] text-white text-[10px] font-bold flex items-center justify-center" data-testid="notif-badge">{notif.unread}</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0 max-h-96 overflow-y-auto" data-testid="notif-panel">
                <div className="px-4 py-3 border-b border-gray-100 font-semibold text-sm">Notifiche</div>
                {notif.items.length === 0 && <p className="px-4 py-6 text-sm text-gray-400 text-center">Nessuna notifica.</p>}
                {notif.items.map((n) => (
                  <div key={n.id} className="px-4 py-3 border-b border-gray-50 hover:bg-gray-50">
                    <p className="text-sm font-medium text-gray-900">{n.title}</p>
                    <p className="text-xs text-gray-500">{n.body}</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">{new Date(n.created_at).toLocaleString("it-IT")}</p>
                  </div>
                ))}
              </PopoverContent>
            </Popover>
            <a href="/" target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" className="rounded-full border-gray-300 bg-white hover:bg-gray-50" data-testid="view-site">
                <ExternalLink className="h-4 w-4 mr-2" /> Vedi il sito
              </Button>
            </a>
          </div>
        </header>
        <ImpersonationBanner />
        <main className="p-6 lg:p-8 max-w-6xl">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
